/**
 * Pure pace / race-prediction math for the public tools (RUN-12).
 *
 * Deliberately mirrors the formulas the product already uses on the backend
 * (`running-coach-agents/src/utils/fitness_metrics.py`) so the free public
 * calculators agree with what a signed-in runner sees:
 *   - Riegel: T2 = T1 * (D2/D1)^1.06  (race-time extrapolation)
 *   - Daniels' VDOT regression        (equivalent-effort cross-check)
 *
 * No React, no I/O — safe to import from server components and to unit-reason
 * about.
 */

/** Canonical Riegel exponent. Matches RIEGEL_EXPONENT in the backend. */
export const RIEGEL_EXPONENT = 1.06;

export interface RaceDistanceDef {
  key: string;
  /** meters */
  meters: number;
  labelEn: string;
  labelPt: string;
}

export const RACE_DISTANCES: RaceDistanceDef[] = [
  { key: "5k", meters: 5000, labelEn: "5K", labelPt: "5K" },
  { key: "10k", meters: 10000, labelEn: "10K", labelPt: "10K" },
  {
    key: "half_marathon",
    meters: 21097.5,
    labelEn: "Half Marathon",
    labelPt: "Meia Maratona",
  },
  {
    key: "marathon",
    meters: 42195,
    labelEn: "Marathon",
    labelPt: "Maratona",
  },
];

export function distanceByKey(key: string): RaceDistanceDef | undefined {
  return RACE_DISTANCES.find((d) => d.key === key);
}

/** Format a number of seconds as H:MM:SS or M:SS. */
export function formatDuration(totalSeconds: number): string {
  if (!isFinite(totalSeconds) || totalSeconds <= 0) return "—";
  const s = Math.round(totalSeconds);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => n.toString().padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${m}:${pad(sec)}`;
}

/** Format a pace (seconds per unit) as M:SS. */
export function formatPace(secondsPerUnit: number): string {
  if (!isFinite(secondsPerUnit) || secondsPerUnit <= 0) return "—";
  const s = Math.round(secondsPerUnit);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${sec.toString().padStart(2, "0")}`;
}

/** Parse a "H:MM:SS" / "MM:SS" / "M:SS" string into seconds. Returns null if invalid. */
export function parseDuration(input: string): number | null {
  const trimmed = input.trim();
  if (!trimmed) return null;
  const parts = trimmed.split(":").map((p) => p.trim());
  if (parts.some((p) => p === "" || isNaN(Number(p)))) return null;
  const nums = parts.map(Number);
  if (nums.some((n) => n < 0)) return null;
  if (nums.length === 3) return nums[0] * 3600 + nums[1] * 60 + nums[2];
  if (nums.length === 2) return nums[0] * 60 + nums[1];
  if (nums.length === 1) return nums[0];
  return null;
}

export const KM_PER_MILE = 1.609344;

/** Pace per km and per mile for a finish time over a distance in meters. */
export function paceFor(
  finishSeconds: number,
  meters: number,
): { perKm: number; perMile: number } {
  const km = meters / 1000;
  const perKm = finishSeconds / km;
  return { perKm, perMile: perKm * KM_PER_MILE };
}

/** Even-split table for a finish time over a distance, in km increments. */
export function kmSplits(
  finishSeconds: number,
  meters: number,
): { km: number; cumulativeSeconds: number }[] {
  const { perKm } = paceFor(finishSeconds, meters);
  const fullKm = Math.floor(meters / 1000);
  const splits: { km: number; cumulativeSeconds: number }[] = [];
  for (let k = 1; k <= fullKm; k++) {
    splits.push({ km: k, cumulativeSeconds: perKm * k });
  }
  const remainder = meters / 1000 - fullKm;
  if (remainder > 0.05) {
    splits.push({
      km: Math.round((fullKm + remainder) * 100) / 100,
      cumulativeSeconds: finishSeconds,
    });
  }
  return splits;
}

/**
 * Riegel extrapolation: predict the time for `targetMeters` from a known
 * effort (`knownSeconds` over `knownMeters`). T2 = T1 * (D2/D1)^k.
 */
export function riegelPredict(
  knownSeconds: number,
  knownMeters: number,
  targetMeters: number,
): number {
  if (knownSeconds <= 0 || knownMeters <= 0 || targetMeters <= 0) return 0;
  return knownSeconds * Math.pow(targetMeters / knownMeters, RIEGEL_EXPONENT);
}

/**
 * Daniels' VDOT from a time/distance — used as a cross-check on Riegel and to
 * surface an estimated VO2max-equivalent. Mirrors `_vdot_from_time_distance`.
 */
export function vdotFromEffort(
  timeSeconds: number,
  meters: number,
): number | null {
  if (timeSeconds <= 0 || meters <= 0) return null;
  const minutes = timeSeconds / 60;
  const velocity = meters / minutes; // m/min
  const vo2 =
    -4.6 + 0.182258 * velocity + 0.000104 * velocity * velocity;
  const pctMax =
    0.8 +
    0.1894393 * Math.exp(-0.012778 * minutes) +
    0.2989558 * Math.exp(-0.1932605 * minutes);
  const vdot = vo2 / pctMax;
  return isFinite(vdot) && vdot > 0 ? vdot : null;
}

export interface RacePrediction {
  distance: RaceDistanceDef;
  finishSeconds: number;
  perKm: number;
  perMile: number;
}

/**
 * Predict finish times across all standard distances from one recent result.
 * Returns a band-friendly point estimate per distance (the UI widens it into
 * a range, mirroring the in-app predictor which never shows a single number).
 */
export function predictAllDistances(
  knownSeconds: number,
  knownMeters: number,
): RacePrediction[] {
  return RACE_DISTANCES.map((distance) => {
    const finishSeconds = riegelPredict(
      knownSeconds,
      knownMeters,
      distance.meters,
    );
    const { perKm, perMile } = paceFor(finishSeconds, distance.meters);
    return { distance, finishSeconds, perKm, perMile };
  });
}

/**
 * Widen a point estimate into a ±band. Riegel error grows with distance, so
 * the band is asymmetric and wider for big extrapolations — honest about
 * uncertainty, as the in-app predictor is.
 */
export function predictionBand(
  finishSeconds: number,
  ratio: number,
): { low: number; high: number } {
  // ratio = target/known distance ratio; larger jumps => wider band.
  const spread = Math.min(
    0.08,
    0.02 + Math.abs(Math.log(ratio)) * 0.025,
  );
  return {
    low: finishSeconds * (1 - spread),
    high: finishSeconds * (1 + spread),
  };
}
